import { useState, useEffect, type FormEvent } from 'react';
import type { FinCounterpartyRow, UpsertCounterpartyInput } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconX } from '@shared/ui/icons';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  FormErrorBox,
  FormGroup,
  FormRow,
  FormRow3,
  Label,
  Input,
  Select,
  BankRequisitesDivider,
  ModalFooter,
} from './styled';

interface CounterpartyModalProps {
  open: boolean;
  onClose: () => void;
  editingItem: FinCounterpartyRow | null;
  onSave: (payload: UpsertCounterpartyInput) => Promise<void>;
  isSaving: boolean;
}

export function CounterpartyModal({
  open,
  onClose,
  editingItem,
  onSave,
  isSaving,
}: CounterpartyModalProps) {
  const [formName, setFormName] = useState('');
  const [formFullName, setFormFullName] = useState('');
  const [formInn, setFormInn] = useState('');
  const [formKpp, setFormKpp] = useState('');
  const [formOgrn, setFormOgrn] = useState('');
  const [formType, setFormType] = useState('supplier');
  const [formAccount, setFormAccount] = useState('');
  const [formBik, setFormBik] = useState('');
  const [formBankName, setFormBankName] = useState('');
  const [formCorrAccount, setFormCorrAccount] = useState('');
  const [formCategory, setFormCategory] = useState('materials');
  const [formNotes, setFormNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (editingItem) {
      setFormName(editingItem.name);
      setFormFullName(editingItem.full_name ?? '');
      setFormInn(editingItem.inn);
      setFormKpp(editingItem.kpp ?? '');
      setFormOgrn(editingItem.ogrn ?? '');
      setFormType(editingItem.counterparty_type);
      setFormAccount(editingItem.account_number ?? '');
      setFormBik(editingItem.bik ?? '');
      setFormBankName(editingItem.bank_name ?? '');
      setFormCorrAccount(editingItem.corr_account ?? '');
      setFormCategory(editingItem.default_category ?? 'materials');
      setFormNotes(editingItem.notes ?? '');
      setFormError(null);
    } else {
      setFormName('');
      setFormFullName('');
      setFormInn('');
      setFormKpp('');
      setFormOgrn('');
      setFormType('supplier');
      setFormAccount('');
      setFormBik('');
      setFormBankName('');
      setFormCorrAccount('');
      setFormCategory('materials');
      setFormNotes('');
      setFormError(null);
    }
  }, [editingItem, open]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      setFormError('Наименование контрагента обязательно');
      return;
    }
    const cleanInn = formInn.trim();
    if (!cleanInn || !/^(\d{10}|\d{12})$/.test(cleanInn)) {
      setFormError('ИНН должен содержать 10 или 12 цифр');
      return;
    }
    if (formAccount && !/^\d{20}$/.test(formAccount.trim())) {
      setFormError('Расчётный счёт должен состоять ровно из 20 цифр');
      return;
    }
    if (formBik && !/^\d{9}$/.test(formBik.trim())) {
      setFormError('БИК должен состоять ровно из 9 цифр');
      return;
    }

    try {
      setFormError(null);
      const payload: UpsertCounterpartyInput = {
        name: formName.trim(),
        full_name: formFullName.trim() || undefined,
        inn: cleanInn,
        kpp: formKpp.trim() || undefined,
        ogrn: formOgrn.trim() || undefined,
        counterparty_type: formType,
        account_number: formAccount.trim() || undefined,
        bik: formBik.trim() || undefined,
        bank_name: formBankName.trim() || undefined,
        corr_account: formCorrAccount.trim() || undefined,
        default_category: formCategory,
        notes: formNotes.trim() || undefined,
        source: editingItem ? editingItem.source : 'manual',
      };

      await onSave(payload);
      onClose();
    } catch (err: unknown) {
      setFormError(`Не удалось сохранить контрагента: ${String(err)}`);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {editingItem ? `Редактирование: ${editingItem.name}` : 'Новый контрагент'}
          </ModalTitle>
          <Button $variant="ghost" $size="sm" onClick={onClose} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </ModalHeader>

        <ModalBody onSubmit={handleSubmit}>
          {formError && (
            <FormErrorBox>
              {formError}
            </FormErrorBox>
          )}

          <FormGroup>
            <Label>Рабочее наименование (для реестра)*</Label>
            <Input
              required
              placeholder="Например, ООО «СеверТрансГрупп»"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Полное юридическое наименование</Label>
            <Input
              placeholder="Общество с ограниченной ответственностью «СеверТрансГрупп»"
              value={formFullName}
              onChange={(e) => setFormFullName(e.target.value)}
            />
          </FormGroup>

          <FormRow3>
            <FormGroup>
              <Label>ИНН (10 или 12 цифр)*</Label>
              <Input
                required
                maxLength={12}
                placeholder="7701234567"
                value={formInn}
                onChange={(e) => setFormInn(e.target.value.replace(/\D/g, ''))}
              />
            </FormGroup>
            <FormGroup>
              <Label>КПП (9 цифр)</Label>
              <Input
                maxLength={9}
                placeholder="770101001"
                value={formKpp}
                onChange={(e) => setFormKpp(e.target.value.replace(/\D/g, ''))}
              />
            </FormGroup>
            <FormGroup>
              <Label>ОГРН</Label>
              <Input
                maxLength={15}
                placeholder="1157746000000"
                value={formOgrn}
                onChange={(e) => setFormOgrn(e.target.value.replace(/\D/g, ''))}
              />
            </FormGroup>
          </FormRow3>

          <FormRow>
            <FormGroup>
              <Label>Тип контрагента</Label>
              <Select value={formType} onChange={(e) => setFormType(e.target.value)}>
                <option value="supplier">Поставщик материалов/оборудования</option>
                <option value="subcontractor">Субподрядчик СМР</option>
                <option value="customer">Заказчик / Генподрядчик</option>
                <option value="leasing">Лизинговая компания</option>
                <option value="bank">Банк / Кредитор</option>
                <option value="tax_authority">ФНС / Налоговый орган</option>
                <option value="other">Прочее</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Статья затрат по умолчанию</Label>
              <Select value={formCategory} onChange={(e) => setFormCategory(e.target.value)}>
                <option value="materials">Материалы и МТО</option>
                <option value="subcontract">Субподрядные работы</option>
                <option value="logistics">Транспорт и доставка</option>
                <option value="leasing">Лизинг техники</option>
                <option value="payroll">Оплата труда</option>
                <option value="taxes">Налоги и сборы</option>
                <option value="services">Услуги и сервис</option>
              </Select>
            </FormGroup>
          </FormRow>

          <BankRequisitesDivider>
            Банковские реквизиты (для выгрузки в 1С:Клиент-Банк)
          </BankRequisitesDivider>

          <FormRow>
            <FormGroup>
              <Label>Расчётный счёт (ровно 20 цифр)</Label>
              <Input
                placeholder="40702810..."
                value={formAccount}
                onChange={(e) => setFormAccount(e.target.value.replace(/\D/g, ''))}
                maxLength={20}
              />
            </FormGroup>
            <FormGroup>
              <Label>БИК банка (ровно 9 цифр)</Label>
              <Input
                placeholder="044525..."
                value={formBik}
                onChange={(e) => setFormBik(e.target.value.replace(/\D/g, ''))}
                maxLength={9}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Наименование банка</Label>
              <Input
                placeholder="ПАО СБЕРБАНК г. Москва"
                value={formBankName}
                onChange={(e) => setFormBankName(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Корр. счёт банка (20 цифр)</Label>
              <Input
                placeholder="30101810..."
                value={formCorrAccount}
                onChange={(e) => setFormCorrAccount(e.target.value.replace(/\D/g, ''))}
                maxLength={20}
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>Примечания</Label>
            <Input
              placeholder="Номер рамочного договора, менеджер, контакты…"
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button $variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button
            $variant="primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
}
